/**
 * Free 200 GBUX connect rewards — Grudge ID / email / third-party wallet.
 * Claimable if already linked. Stay-logged-in scope for dApp.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useWalletState } from "@/contexts/wallet-context";
import {
  getGrudgeToken,
  isLoggedIn,
  loginWithGrudgeId,
} from "@/lib/grudge-auth";
import { FLEET } from "@shared/fleet";
import { Gift, Loader2, Mail, Shield, Wallet } from "lucide-react";

const BONUS = 200;

type ClaimStatus = {
  grudgeId: boolean;
  email: boolean;
  wallet: boolean;
  playGbux?: number;
};

export function ConnectRewardsModal(props: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { wallet, openWalletPanel, refreshBalances, connect } =
    useWalletState();
  const [status, setStatus] = useState<ClaimStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const pk = wallet.publicKey;
    if (!pk) {
      setStatus({ grudgeId: false, email: false, wallet: false });
      return;
    }
    try {
      const h = new Headers({ Accept: "application/json" });
      h.set("X-Wallet-Address", pk);
      const t = getGrudgeToken();
      if (t) h.set("Authorization", `Bearer ${t}`);
      const r = await fetch(`/api/play/connect-bonus?wallet=${encodeURIComponent(pk)}`, {
        headers: h,
      });
      const j = (await r.json()) as ClaimStatus & { error?: string };
      if (r.ok) setStatus(j);
    } catch {
      /* ignore */
    }
  }, [wallet.publicKey]);

  useEffect(() => {
    if (props.open) void load();
  }, [props.open, load]);

  const claim = async (kind: "grudge_id" | "email" | "wallet") => {
    setBusy(kind);
    setErr(null);
    setMsg(null);
    try {
      const pk = wallet.publicKey;
      if (!pk && kind === "wallet") {
        await connect();
        throw new Error("Connect a wallet, then claim again");
      }
      if (kind === "grudge_id" && !isLoggedIn()) {
        loginWithGrudgeId(
          typeof window !== "undefined"
            ? window.location.pathname + window.location.search
            : "/lobby",
        );
        return;
      }
      if (kind === "email" && !isLoggedIn()) {
        loginWithGrudgeId(
          typeof window !== "undefined"
            ? window.location.pathname + "?claim=email"
            : "/account",
        );
        return;
      }
      const h = new Headers({ "Content-Type": "application/json" });
      if (pk) h.set("X-Wallet-Address", pk);
      const t = getGrudgeToken();
      if (t) h.set("Authorization", `Bearer ${t}`);
      const r = await fetch("/api/play/connect-bonus", {
        method: "POST",
        headers: h,
        body: JSON.stringify({
          kind,
          wallet: pk,
        }),
      });
      const j = (await r.json()) as {
        error?: string;
        message?: string;
        playGbux?: number;
        credited?: number;
      };
      if (!r.ok) throw new Error(j.error || "Claim failed");
      setMsg(
        j.message ||
          `+${j.credited ?? BONUS} play GBUX credited to your server wallet`,
      );
      void refreshBalances();
      void load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const Row = ({
    kind,
    title,
    blurb,
    claimed,
    icon,
  }: {
    kind: "grudge_id" | "email" | "wallet";
    title: string;
    blurb: string;
    claimed?: boolean;
    icon: ReactNode;
  }) => (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        padding: "12px 0",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div style={{ color: "#fbbf24", marginTop: 2 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#f8fafc", fontSize: 14 }}>
          {title}{" "}
          <span style={{ color: "#4ade80" }}>+{BONUS} GBUX</span>
        </div>
        <p style={{ fontSize: 12, color: "rgba(148,163,184,0.95)", marginTop: 4 }}>
          {blurb}
        </p>
      </div>
      <Button
        size="sm"
        disabled={Boolean(claimed) || busy === kind}
        onClick={() => void claim(kind)}
        className={
          claimed
            ? "bg-emerald-900/40 text-emerald-300 border border-emerald-500/30"
            : "bg-amber-600 hover:bg-amber-500 text-black font-bold"
        }
      >
        {busy === kind ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : claimed ? (
          "Claimed"
        ) : (
          "Claim"
        )}
      </Button>
    </div>
  );

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md border-amber-500/30 bg-[#0c1220] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Gift className="h-5 w-5 text-amber-400" />
            Free 200 $GBUX each
          </DialogTitle>
          <DialogDescription className="text-white/65">
            Real <strong className="text-emerald-300">play-ledger</strong> GBUX
            (tables / slots / BJ). Claim each identity layer once if already
            linked.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-amber-500/25 bg-amber-950/20 px-3 py-2 text-[11px] text-amber-50/90 space-y-1.5">
          <div className="font-bold text-amber-200">Free GBUX policy (clear)</div>
          <ul className="list-disc pl-4 space-y-0.5 text-white/70">
            <li>
              <strong className="text-emerald-300">Welcome 200</strong> — once,
              first wallet play account (automatic).
            </li>
            <li>
              <strong className="text-amber-200">+200 Grudge ID</strong> ·{" "}
              <strong className="text-amber-200">+200 email</strong> ·{" "}
              <strong className="text-amber-200">+200 inject wallet</strong> —
              optional link rewards, each once (not a second welcome).
            </li>
            <li>
              Max free path: <strong>200 + 600 = 800</strong> play GBUX if you
              claim all three. Not accidental stacking of the same grant.
            </li>
          </ul>
        </div>

        <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-[11px] text-white/55 space-y-1">
          <div className="flex items-center gap-1.5 text-sky-200/90">
            <Shield className="h-3.5 w-3.5" /> Web3 scope
          </div>
          <p>
            Message sign / deposit tx only — no seeds. Stay connected: silent
            rebind + 30m play session (tables · slots · BJ). Support:{" "}
            <a className="text-amber-300 underline" href={FLEET.supportMailto}>
              {FLEET.supportEmail}
            </a>
          </p>
        </div>

        <Row
          kind="grudge_id"
          title="Grudge ID / Puter auth"
          blurb="Sign in at id.grudge-studio.com (SSO). Claimable if you already have a session."
          claimed={status?.grudgeId}
          icon={<Shield className="h-5 w-5" />}
        />
        <Row
          kind="email"
          title="Email on Grudge ID"
          blurb="Link email on your Grudge ID profile, then claim. Same JWT session."
          claimed={status?.email}
          icon={<Mail className="h-5 w-5" />}
        />
        <Row
          kind="wallet"
          title="Third-party wallet"
          blurb="Phantom, Solflare, or other inject — best practice: opt-in connect, no auto-connect on load."
          claimed={status?.wallet}
          icon={<Wallet className="h-5 w-5" />}
        />

        {!wallet.publicKey && (
          <Button
            variant="outline"
            className="w-full border-violet-400/40"
            onClick={() => openWalletPanel("connect")}
          >
            Open connect panel
          </Button>
        )}

        {err && (
          <p className="text-xs text-red-300">{err}</p>
        )}
        {msg && (
          <p className="text-xs text-emerald-300">{msg}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
